import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  Building2,
  Phone,
  Mail,
  MapPin,
  Image,
  Percent
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BusinessInfo() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Business Information
  const [businessInfo, setBusinessInfo] = useState({
    business_name: "",
    business_address: "",
    business_phone: "",
    business_email: "",
    business_logo: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    enabled: false,
    tax_name: "VAT",
    tax_rate: 0,
    tax_id: "",
    include_in_price: false
  });

  const authHeaders = {
    headers: { Authorization: `Bearer ${accessToken}` }
  };

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, authHeaders);
      const settings = response.data;
      
      setBusinessInfo({
        business_name: settings.business_name || "",
        business_address: settings.business_address || "",
        business_phone: settings.business_phone || "",
        business_email: settings.business_email || "",
        business_logo: settings.business_logo || null,
      });
      
      if (settings.business_logo) {
        setLogoPreview(settings.business_logo);
      }
      
      if (settings.tax_settings) {
        setTaxSettings(settings.tax_settings);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load business settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file size must be less than 2MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result;
      setLogoPreview(base64);
      setBusinessInfo((prev) => ({ ...prev, business_logo: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setBusinessInfo((prev) => ({ ...prev, business_logo: null }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        business_name: businessInfo.business_name,
        business_address: businessInfo.business_address,
        business_phone: businessInfo.business_phone,
        business_email: businessInfo.business_email,
        business_logo: businessInfo.business_logo,
        tax_settings: taxSettings
      }, authHeaders);

      toast.success("Business information saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="container mx-auto px-6 py-8 max-w-3xl">
          <Skeleton className="h-96" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-extrabold tracking-tight text-primary">
                  Business Information
                </h1>
                <p className="text-sm text-muted-foreground">Manage your company details and branding</p>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-business-info"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-3xl">
        <div className="space-y-6">
          {/* Company Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Company Details
              </CardTitle>
              <CardDescription>
                This information will appear on invoices, job orders, and payslips
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={businessInfo.business_name}
                  onChange={(e) =>
                    setBusinessInfo((prev) => ({ ...prev, business_name: e.target.value }))
                  }
                  placeholder="Your Company Name"
                  data-testid="input-business-name"
                />
              </div>
              
              <div>
                <Label htmlFor="business_address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </Label>
                <Textarea
                  id="business_address"
                  value={businessInfo.business_address}
                  onChange={(e) =>
                    setBusinessInfo((prev) => ({ ...prev, business_address: e.target.value }))
                  }
                  placeholder="123 Business St, City, State ZIP"
                  rows={3}
                  data-testid="input-business-address"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number
                  </Label>
                  <Input
                    id="business_phone"
                    value={businessInfo.business_phone}
                    onChange={(e) =>
                      setBusinessInfo((prev) => ({ ...prev, business_phone: e.target.value }))
                    }
                    placeholder="(555) 123-4567"
                    data-testid="input-business-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="business_email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </Label>
                  <Input
                    id="business_email"
                    type="email"
                    value={businessInfo.business_email}
                    onChange={(e) =>
                      setBusinessInfo((prev) => ({ ...prev, business_email: e.target.value }))
                    }
                    placeholder="contact@business.com"
                    data-testid="input-business-email"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Logo Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5 text-purple-600" />
                Business Logo
              </CardTitle>
              <CardDescription>
                Upload your company logo (max 2MB, recommended size: 200x200px)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!logoPreview ? (
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Drag and drop your logo here, or click to browse
                  </p>
                  <Input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="logo-upload"
                    onChange={handleLogoUpload}
                    data-testid="logo-upload-input"
                  />
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <Button variant="outline" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </span>
                    </Button>
                  </Label>
                </div>
              ) : (
                <div className="flex items-start gap-6">
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Business Logo"
                      className="h-32 w-32 object-contain rounded-lg border bg-white p-2"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
                      onClick={removeLogo}
                      data-testid="remove-logo-button"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-2">Logo uploaded successfully</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Your logo will appear on invoices, job orders, and other documents.
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="logo-replace"
                      onChange={handleLogoUpload}
                    />
                    <Label htmlFor="logo-replace" className="cursor-pointer">
                      <Button variant="outline" size="sm" asChild>
                        <span>Replace Logo</span>
                      </Button>
                    </Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tax Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-green-600" />
                Tax Settings
              </CardTitle>
              <CardDescription>
                Configure tax calculation for invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <Label htmlFor="tax-enabled" className="text-base font-medium">Enable Tax Calculation</Label>
                  <p className="text-sm text-muted-foreground">Add tax to order totals on invoices</p>
                </div>
                <Switch
                  id="tax-enabled"
                  checked={taxSettings.enabled}
                  onCheckedChange={(checked) => 
                    setTaxSettings(prev => ({ ...prev, enabled: checked }))
                  }
                  data-testid="tax-enabled-switch"
                />
              </div>

              {taxSettings.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label htmlFor="tax-name">Tax Name</Label>
                    <Input
                      id="tax-name"
                      value={taxSettings.tax_name}
                      onChange={(e) =>
                        setTaxSettings(prev => ({ ...prev, tax_name: e.target.value }))
                      }
                      placeholder="VAT"
                      data-testid="input-tax-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tax-rate">Tax Rate (%)</Label>
                    <Input
                      id="tax-rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={taxSettings.tax_rate}
                      onChange={(e) =>
                        setTaxSettings(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))
                      }
                      placeholder="12"
                      data-testid="input-tax-rate"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tax-id">Tax ID / TIN</Label>
                    <Input
                      id="tax-id"
                      value={taxSettings.tax_id}
                      onChange={(e) =>
                        setTaxSettings(prev => ({ ...prev, tax_id: e.target.value }))
                      }
                      placeholder="000-000-000-000"
                      data-testid="input-tax-id"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      id="tax-included"
                      checked={taxSettings.include_in_price}
                      onCheckedChange={(checked) => 
                        setTaxSettings(prev => ({ ...prev, include_in_price: checked }))
                      }
                      data-testid="tax-included-switch"
                    />
                    <Label htmlFor="tax-included" className="text-sm">
                      Prices include tax
                    </Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
